"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListPager = ListPager;
exports.ListPagerTotal = ListPagerTotal;
const jsx_runtime_1 = require("react/jsx-runtime");
const button_1 = require("./button");
const icon_1 = require("./icon");
const select_1 = require("./select");
function ListPager({ page, labels, busy = false, compact = false, }) {
    const { index, count, onGoTo } = page;
    const numbered = index !== undefined && count !== undefined && onGoTo !== undefined;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-1", children: [(0, jsx_runtime_1.jsxs)(button_1.Button, { variant: "ghost", size: "sm", "aria-label": labels.previousPage, onClick: page.onPrevious, disabled: !page.hasPrevious || busy, "aria-busy": busy, className: "gap-1 px-2", children: [(0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-left", size: 14 }), compact ? null : (0, jsx_runtime_1.jsx)("span", { className: "hidden sm:inline", children: labels.previousPage })] }), !numbered ? null : ((0, jsx_runtime_1.jsx)(PageNumbers, { index: index, count: count, disabled: busy, label: labels.pageSelector, format: labels.pageOf, onGoTo: onGoTo })), (0, jsx_runtime_1.jsxs)(button_1.Button, { variant: "ghost", size: "sm", "aria-label": labels.nextPage, onClick: page.onNext, disabled: !page.hasNext || busy, "aria-busy": busy, className: "gap-1 px-2", children: [compact ? null : (0, jsx_runtime_1.jsx)("span", { className: "hidden sm:inline", children: labels.nextPage }), (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "chevron-right", size: 14 })] })] }));
}
/**
 * The result count, for the other end of the footer.
 *
 * ⚠️ **A component rather than a prop on the pager**, because the two are no longer adjacent: the
 * count goes bottom-left and the controls bottom-right. A prop would leave the pager positioning
 * something that is not inside it.
 *
 * ⚠️ It renders nothing without both halves, so a footer that has no total keeps its layout — the
 * spacer either side does the work.
 */
function ListPagerTotal({ total, labels, }) {
    if (total === undefined || labels.pageTotal === undefined)
        return null;
    return (0, jsx_runtime_1.jsx)("span", { className: "tabular-nums", children: labels.pageTotal(total) });
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE WINDOW OF PAGE NUMBERS, AND THE ELLIPSIS THAT OPENS THE REST.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **First and last are ALWAYS drawn.** *"Back to the beginning"* and *"how far does this go"*
 * are the two questions a pager is asked most, and both become a menu the moment the ends are
 * allowed to scroll out of the window.
 *
 * ⚠️ **The window is the current page and one either side**, so the control is at most seven cells
 * wide — first, ellipsis, three, ellipsis, last — whatever the count. A window that grew with the
 * count would reflow the footer as somebody paged through it.
 *
 * ⚠️ **The window is CLAMPED at both ends** so it keeps its width there: on page 1 it runs 1-2-3
 * rather than 1-2, and on the last it runs n-2, n-1, n. Without that the strip narrows at the edges
 * and Previous and Next move under the pointer that is clicking them.
 *
 * ⚠️ **An ellipsis appears only where there is a gap.** Drawn unconditionally it is a lie at four
 * pages, where nothing is hidden.
 */
function PageNumbers({ index, count, disabled, label, format, onGoTo, }) {
    const last = count - 1;
    const from = Math.max(0, Math.min(index - 1, count - 3));
    const to = Math.min(last, Math.max(index + 1, 2));
    const shown = [];
    for (let at = from; at <= to; at += 1)
        shown.push(at);
    const cells = [];
    const first = shown[0] ?? 0;
    const final = shown.at(-1) ?? 0;
    if (first > 0)
        cells.push(0);
    if (first > 1)
        cells.push('gap');
    cells.push(...shown);
    if (final < last - 1)
        cells.push('gap');
    if (final < last)
        cells.push(last);
    return ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: cells.map((cell, at) => cell === 'gap' ? ((0, jsx_runtime_1.jsx)(PageJump, { index: index, count: count, disabled: disabled, label: label, format: format, onGoTo: onGoTo }, at === 0 ? 'gap-leading' : 'gap-trailing')) : ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: cell === index ? 'outline' : 'ghost', size: "sm", disabled: disabled, "aria-current": cell === index ? 'page' : undefined, className: "w-8 px-0 tabular-nums", onClick: () => {
                onGoTo(cell);
            }, children: cell + 1 }, cell))) }));
}
/**
 * The ellipsis — which is a control, not punctuation.
 *
 * ⚠️⚠️ **IT REUSES THE LISTBOX THE PAGER USED TO WEAR, AND THE OLD NOTE STILL HOLDS.** A number
 * field is banned by item 66 (`stepper.tsx` carries the three reasons), and what somebody wants
 * here is *pick a page* — a closed set, the count known, with nothing half-typed to interpret.
 * Searchable past ten, which is the line the toolbar's other pickers already use. What was wrong
 * with it was never the jump; it was that the jump was the only way to move at all.
 *
 * ⚠️ **`triggerLabel` is why the trigger says `…` rather than the page it is sitting on.** Behind
 * an ellipsis is exactly where somebody looks for *"the pages I cannot see"*, and a trigger reading
 * `3` in a row that already draws a 3 is two controls claiming the same thing.
 *
 * ⚠️ **The options are built EVERY RENDER and that is not a leak worth fixing.** A page is capped
 * at 200 rows, so a workspace would need forty thousand rows to make a two-hundred-entry array —
 * and it is rebuilt only when the count changes, because nothing else re-renders the footer.
 */
function PageJump({ index, count, disabled, label, format, onGoTo, }) {
    const options = Array.from({ length: count }, (_ignored, at) => ({
        value: String(at),
        /* ⚠️ `at + 1`: the model is zero-based and people are not. This is the only place that adds it. */
        label: format === undefined ? String(at + 1) : format(at + 1, count),
    }));
    return (
    /* ⚠️⚠️ **THE WIDTH GOES ON A WRAPPER.** `Select`'s outer element is `inline-flex w-full`, so a
       `w-*` passed to the component lands on the inner button while the wrapper goes on demanding
       the whole row — which in this flex row would push Previous and Next apart by the width of the
       footer. `select.tsx` carries the long version. */
    (0, jsx_runtime_1.jsx)("span", { className: "w-9 shrink-0", children: (0, jsx_runtime_1.jsx)(select_1.Select, { value: String(index), triggerLabel: "\u2026", options: options, disabled: disabled, "aria-label": label, searchable: count > 10, 
            /* ⚠️ The search box needs its own name — it is a second control inside the menu, and
               without this it is an unlabelled text field to a screen reader. */
            searchPlaceholder: label, inline: true, className: "h-8 justify-center gap-0 px-1", onChange: (event) => {
                onGoTo(Number(event.target.value));
            } }) }));
}
//# sourceMappingURL=list-pager.js.map